import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject, interval } from 'rxjs';
import { takeUntil, withLatestFrom } from 'rxjs/operators';
import { AppState } from '@store/app.state';
import { FrameActions } from '@store/actions/frame.actions';
import { selectAllFrames, selectCurrentFrameId, selectCurrentFrameIndex, selectFrameCount } from '@store/selectors/frame.selectors';
import { selectSettings } from '@store/selectors/settings.selector';
import { PlaybackService } from '@app/services/playback.service';

@Component({
  selector: 'app-frames-timeline',
  standalone: true,
  imports: [CommonModule, AsyncPipe, FormsModule],
  templateUrl: './frames-timeline.html',
  styleUrls: ['./frames-timeline.css']
})
export class FramesTimeline implements OnInit, OnDestroy {
  frameCount$: Observable<number>;
  currentIndex$: Observable<number>;

  fps = 12;
  isPlaying = false;

  private destroy$ = new Subject<void>();
  private playInterval$ = new Subject<void>();

  constructor(private store: Store<AppState>, private playbackSvc: PlaybackService) {
    this.frameCount$ = this.store.select(selectFrameCount);
    this.currentIndex$ = this.store.select(selectCurrentFrameIndex);
    this.store.select(selectSettings).pipe(takeUntil(this.destroy$)).subscribe(s => { if(s.fps) this.fps = s.fps; });
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.playInterval$.next();
    this.playInterval$.complete();
  }

  togglePlay(): void {
    this.isPlaying ? this.stopPlay() : this.startPlay();
  }

  startPlay(): void {
    this.isPlaying = true;
    this.playbackSvc.setPlaying(true);
    this.playInterval$.next(); // stop any existing
    interval(1000 / this.fps).pipe(
      takeUntil(this.playInterval$),
      takeUntil(this.destroy$),
      withLatestFrom(
        this.store.select(selectAllFrames),
        this.store.select(selectCurrentFrameIndex)
      )
    ).subscribe(([_, frames, idx]) => {
      if (!frames.length) return;
      const next = (idx + 1) % frames.length;
      this.store.dispatch(FrameActions.selectFrame({ frameId: frames[next].id }));
    });
  }

  stopPlay(): void {
    this.isPlaying = false;
    this.playbackSvc.setPlaying(false);
    this.playInterval$.next();
  }

  onFpsChange(): void {
    if (this.isPlaying) {
      this.stopPlay();
      this.startPlay();
    }
  }

  prevFrame(): void {
    this.store.select(selectAllFrames).pipe(
      withLatestFrom(this.store.select(selectCurrentFrameIndex)),
      takeUntil(this.destroy$)
    ).subscribe(([frames, idx]) => {
      if (!frames.length) return;
      const prev = (idx - 1 + frames.length) % frames.length;
      this.store.dispatch(FrameActions.selectFrame({ frameId: frames[prev].id }));
    }).unsubscribe();
  }

  nextFrame(): void {
    this.store.select(selectAllFrames).pipe(
      withLatestFrom(this.store.select(selectCurrentFrameIndex)),
      takeUntil(this.destroy$)
    ).subscribe(([frames, idx]) => {
      if (!frames.length) return;
      const next = (idx + 1) % frames.length;
      this.store.dispatch(FrameActions.selectFrame({ frameId: frames[next].id }));
    }).unsubscribe();
  }
}
